
import { Component, ViewChild } from "@angular/core";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { isMobileScreen } from "@ponder-shared/utils/screenBreakpoints";
import { injectStripe, StripeElementsDirective } from "ngx-stripe";
import { take } from "rxjs";
import { BusySpinComponent } from "@ponder-shared/components/busy-spin.component";
import { PonderAuthClient } from "@apiserver-rest/ponder-auth-client";
import { CreateSubscriptionResponse } from "@apiserver-rest/ponder-auth-client-base";
import { StripeElements } from "@stripe/stripe-js";


@Component({
    imports: [BusySpinComponent],
    templateUrl: "./upgrade-checkout.component.html"
})
export class UpgradeCheckoutComponent {
    @ViewChild(StripeElementsDirective) elements!: StripeElementsDirective;

    public isMobile: boolean = false;
    public isProcessing: boolean = false;
    public internalError: boolean = false;
    public internalErrorText?: string;
    public stripeError: boolean = false;
    public stripeErrorText?: string;

    private stripe = injectStripe();
    private stripeElements!: StripeElements;

    public constructor(private events: EventBroker, private ponderAuthClient: PonderAuthClient) {

    }

    public ngOnDestroy(): void {
        this.events.ui.restoreContentPaddingEvent.emit();
    }

    public ngOnInit(): void {
        this.isMobile = isMobileScreen();
        this.events.ui.hideNavbarEvent.emit();
        this.events.ui.removeContentPaddingEvent.emit();
        
        this.stripe.elements({
            mode: "subscription",
            amount: 500,
            currency: "usd",
            // https://docs.stripe.com/elements/appearance-api
            appearance: {
                theme: "night",
                variables: {
                    borderRadius: "4px",
                    colorText: "#ffffff",
                    colorTextSecondary: "#94a3b8",      // slate-400
                    fontSizeSm: "0.875rem",             // text-sm
                    colorDanger: "#dc2626"
                },
                rules: {
                    ".Input": {
                        boxShadow: "none",
                        border: "none"
                    },
                    ".Input:focus": {
                        boxShadow: "0px 0px 5px #3C61CF"
                    }
                }
            }
        }).subscribe((elements: StripeElements) => {
            this.stripeElements = elements;
            const paymentElement = elements.create("payment", { layout: "tabs" })
            paymentElement.mount("#payment-element");
        });
    }

    public fetchUpdates(): void {
        this.elements.fetchUpdates();
    }

    public onSubmit(): void {
        if (this.isProcessing) {
            return;
        }

        this.isProcessing = true;
        this.stripeError = false;
        this.stripeErrorText = undefined;

        this.validatePayment();
    }

    /**
     * First we validate the payment information.
     */
    private validatePayment(): void {
        this.stripeElements.submit().then((response) => {
            if (response.error) {
                this.stripeError = true;
                this.isProcessing = false;
                this.stripeErrorText = response.error.message;
            } else {
                this.createSubscription();
            }
        }).catch(() => {
            this.stripeError = true;
            this.isProcessing = false;
        });
    }

    /**
     * Next we call our backend to create the pending subscription for this user and the selected product.
     */
    private createSubscription(): void {
        this.ponderAuthClient
            .subscriptionCreate()
            .pipe(take(1))
            .subscribe({
                next: (response: CreateSubscriptionResponse) => {
                    if (response.success && response.stripe) {
                        if (!response.stripe.error && (response.stripe.clientSecret && response.stripe.id)) {
                            this.applyPayment(response.stripe.clientSecret, response.stripe.id);
                        } else {
                            this.internalError = true;
                            this.internalErrorText = response.stripe.error;
                        }
                    } else {
                        this.internalError = true;
                        this.isProcessing = false;
                    }
                },
                error: () => {
                    this.internalError = true;
                    this.isProcessing = false;
                }
            });
    }

    /**
     * Finally, we attempt to apply the payment information to the pending subscription.
     * 
     * @param clientSecret
     * @param subscriptionId 
     */
    private applyPayment(clientSecret: string, subscriptionId: string): void {
        this.stripe.confirmPayment({
            elements: this.stripeElements,
            clientSecret: clientSecret,
            confirmParams: {
                return_url: "https://pondertcg.com/account/upgrade/complete"
            }
        }).pipe(take(1)).subscribe({
            next: (stripeResponse) => {
                if (stripeResponse.error) {
                    this.stripeError = true;
                    this.stripeErrorText = stripeResponse.error.message;
                }
            },
            error: () => {
                this.stripeError = true;
                this.stripeErrorText = "Unknown error with Stripe service.";
            }
        }).add(() => {
            if (this.stripeError) {
                // Something went wrong applying the payment. Cancel the subscription as any retry will create a new one.
                this.ponderAuthClient.subscriptionCancel(subscriptionId, true).pipe(take(1)).subscribe();
            }
            this.isProcessing = false;
        });
    }
}